import { mergeAssistantTextPartial } from "@/lib/assistant-text-normalization";
import type { ConversationMessage } from "@/stores/conversation-message-types";

export interface PendingStreamUpdate {
  executionId: string;
  stepId?: string;
  content: string;
  createdAt?: string;
  sessionId?: string;
  conversationFrameId?: string;
}

export function applyStreamingBatch(
  state: { messages: ConversationMessage[] },
  batch: PendingStreamUpdate[],
): { messages: ConversationMessage[] } | typeof state {
  // Build indexes once per batch instead of scanning messages per update.
  const draftIndexByStep = new Map<string, number>();
  const committedSteps = new Set<string>();
  const hasSavedByExec = new Set<string>();
  for (let i = 0; i < state.messages.length; i++) {
    const m = state.messages[i]!;
    if (!m.executionId) continue;
    const identity = streamIdentity(m.executionId, m.stepId);
    if (m.role === "assistant" && m.isStreaming) {
      draftIndexByStep.set(identity, i);
    } else if (m.role === "assistant") {
      hasSavedByExec.add(m.executionId);
    } else if (m.activityKind === "progress" && m.stepId) {
      committedSteps.add(identity);
    }
  }

  let messages: ConversationMessage[] | null = null;
  const appended: ConversationMessage[] = [];

  for (const update of batch) {
    const { executionId, stepId } = update;
    if (hasSavedByExec.has(executionId)) continue;
    const identity = streamIdentity(executionId, stepId);
    if (committedSteps.has(identity)) continue;
    const text = update.content;
    if (!text) continue;
    const idx = draftIndexByStep.get(identity);
    if (idx === undefined) {
      appended.push({
        id: streamMessageId(executionId, stepId),
        role: "assistant",
        content: text,
        executionId,
        stepId,
        sessionId: update.sessionId,
        conversationFrameId: update.conversationFrameId,
        createdAt: update.createdAt ?? new Date().toISOString(),
        isStreaming: true,
      });
      continue;
    }
    const existing = (messages ?? state.messages)[idx]!;
    const nextContent = mergeStreamingContent(existing.content, text);
    if (nextContent === existing.content) continue;
    if (messages === null) messages = [...state.messages];
    messages[idx] = {
      ...existing,
      content: nextContent,
      sessionId: existing.sessionId ?? update.sessionId,
      conversationFrameId:
        existing.conversationFrameId ?? update.conversationFrameId,
    };
  }

  if (appended.length === 0 && messages === null) return state;
  const next = messages ?? [...state.messages];
  if (appended.length > 0) next.push(...appended);
  return { messages: next };
}

export function commitStreamingProgress(
  state: { messages: ConversationMessage[] },
  msg: ConversationMessage,
): { messages: ConversationMessage[] } | typeof state {
  const executionId = msg.executionId;
  if (!executionId) return state;
  const identity = streamIdentity(executionId, msg.stepId);
  const alreadyCommitted = state.messages.some(
    (existing) =>
      existing.activityKind === "progress" &&
      existing.executionId === executionId &&
      (existing.id === msg.id ||
        (Boolean(msg.stepId) &&
          streamIdentity(executionId, existing.stepId) === identity)),
  );
  let draftIndex = -1;
  let legacyDraftIndex = -1;
  for (let index = state.messages.length - 1; index >= 0; index--) {
    const candidate = state.messages[index]!;
    if (!candidate.isStreaming || candidate.executionId !== executionId) {
      continue;
    }
    if (msg.stepId && !candidate.stepId && legacyDraftIndex === -1) {
      legacyDraftIndex = index;
    }
    if (!msg.stepId || candidate.stepId === msg.stepId) {
      draftIndex = index;
      break;
    }
  }
  if (draftIndex === -1) draftIndex = legacyDraftIndex;
  if (alreadyCommitted) {
    if (draftIndex === -1) return state;
    return {
      messages: state.messages.filter((_, index) => index !== draftIndex),
    };
  }
  const committed = { ...msg, isStreaming: false };
  if (draftIndex === -1) {
    return { messages: [...state.messages, committed] };
  }
  const messages = [...state.messages];
  messages[draftIndex] = committed;
  return { messages };
}

export function streamIdentity(executionId: string, stepId?: string): string {
  return stepId ? `${executionId}\u0000${stepId}` : executionId;
}

function streamMessageId(executionId: string, stepId?: string): string {
  return stepId ? `stream-${executionId}-${stepId}` : `stream-${executionId}`;
}

function mergeStreamingContent(existing: string, incoming: string): string {
  return mergeAssistantTextPartial(existing, incoming);
}
